import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { X, Search, Plus } from "lucide-react";

if (typeof window !== "undefined" && document.getElementById("root")) {
  Modal.setAppElement("#root");
}

const formatPrice = (val) => {
  if (val == null || val === "") return "₱0";
  const n = Number(val);
  return isNaN(n) ? String(val) : `₱${n.toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;
};

const AddItemModal = ({ isOpen, onClose, onAdd, merchantId, searchItems = [] }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (isOpen && searchItems?.length) {
      setSearchResults(searchItems);
    } else if (isOpen) {
      setSearchResults([]);
    }
  }, [isOpen, searchItems]);

  const handleSearch = () => {
    setSearching(true);
    const q = searchQuery.trim().toLowerCase();
    const items = searchItems?.length ? searchItems : [];
    const filtered = q
      ? items.filter(
          (i) =>
            (i.sku || "").toLowerCase().includes(q) ||
            (i.item || i.description || i.name || i.productName || "").toLowerCase().includes(q)
        )
      : items;
    setSearchResults(filtered);
    setSearching(false);
  };

  const handleAddSuggested = (item) => {
    const existing = selectedItems.find((s) => (s.sku || s.id) === (item.sku || item.id));
    if (existing) {
      setSelectedItems((prev) =>
        prev.map((s) =>
          (s.sku || s.id) === (item.sku || item.id)
            ? { ...s, qty: (s.qty || 1) + 1 }
            : s
        )
      );
    } else {
      const invId = item.inventoryId ?? item.id;
      setSelectedItems((prev) => [
        ...prev,
        {
          id: invId,
          inventoryId: invId,
          productId: item.productId,
          sku: item.sku || item.productSku || "-",
          description: item.item || item.description || item.name || item.productName || "-",
          qty: 1,
          uom: item.uom || item.stockUom || "Box",
          price: item.cost ?? item.price ?? 0
        }
      ]);
    }
  };

  const handleRemoveSelected = (idx) => {
    setSelectedItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleConfirmAdd = () => {
    if (selectedItems.length && onAdd) {
      onAdd(selectedItems);
    }
    setSelectedItems([]);
    setSearchQuery("");
    setSearchResults([]);
    onClose();
  };

  const handleCancel = () => {
    setSelectedItems([]);
    setSearchQuery("");
    setSearchResults([]);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleCancel}
      style={{
        overlay: { backgroundColor: "rgba(0, 0, 0, 0.6)", zIndex: 1000 },
        content: {
          position: "relative",
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          maxWidth: "680px",
          width: "100%",
          maxHeight: "90vh",
          margin: "auto",
          padding: "0",
          outline: "none",
          boxShadow: "0 20px 50px rgba(0, 0, 0, 0.15)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column"
        }
      }}
      contentLabel="Add Item"
    >
      <div className="add-item-modal-content">
        <button className="add-item-modal-close" onClick={handleCancel} aria-label="Close">
          <X size={24} />
        </button>
        <h2 className="add-item-modal-title">Add Item</h2>

        {/* Search Section */}
        <div className="add-item-modal-search">
          <div className="add-item-modal-search-input-wrap">
            <Search className="add-item-modal-search-icon" size={18} />
            <input
              type="text"
              className="add-item-modal-search-input"
              placeholder="Gatorade Blue Bolt"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>
          <button type="button" className="add-item-modal-btn-search" onClick={handleSearch}>
            Search
          </button>
          <button type="button" className="add-item-modal-btn-scan">
            Scan Barcode
          </button>
        </div>

        {/* Search Results */}
        <div className="add-item-modal-results">
          <h3 className="add-item-modal-section-title">Suggested Items</h3>
          <div className="add-item-modal-results-list">
            {searching ? (
              <div className="add-item-modal-empty">Searching...</div>
            ) : searchResults.length === 0 ? (
              <div className="add-item-modal-empty">Enter a search term and click Search</div>
            ) : (
              searchResults.map((item, idx) => (
                <div key={item.sku || item.id || idx} className="add-item-modal-result-row">
                  <span className="add-item-modal-result-sku">{item.sku || item.productSku || "-"}</span>
                  <span className="add-item-modal-result-desc">
                    {item.item || item.description || item.name || item.productName || "-"}
                  </span>
                  <span className="add-item-modal-result-price">
                    {formatPrice(item.cost ?? item.price ?? 0)}
                  </span>
                  <button
                    type="button"
                    className="add-item-modal-result-add"
                    onClick={() => handleAddSuggested(item)}
                  >
                    <Plus size={18} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Selected Items Table */}
        <div className="add-item-modal-selected">
          <div className="add-item-modal-table-wrap">
            <table className="add-item-modal-table">
              <thead>
                <tr>
                  <th>SKU Code</th>
                  <th>Item Description</th>
                  <th>QTY</th>
                  <th>Unit of Measure</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {selectedItems.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.sku}</td>
                    <td>{row.description}</td>
                    <td>{row.qty}</td>
                    <td>{row.uom}</td>
                    <td>{formatPrice(row.price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="add-item-modal-footer">
          <button type="button" className="add-item-modal-btn-cancel" onClick={handleCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="add-item-modal-btn-add"
            onClick={handleConfirmAdd}
            disabled={selectedItems.length === 0}
          >
            Add
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddItemModal;
